import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class LoadingService {
    private loadingSubject = new BehaviorSubject<boolean>(false);
    private loadingMap = new Map<string, boolean>();

    // Observable that components can subscribe to
    public loading$: Observable<boolean> = this.loadingSubject.asObservable().pipe(
        debounceTime(100), // Prevent flicker for very quick requests
        distinctUntilChanged()
    );

    constructor() { }

    /**
     * Show loading indicator
     * @param key Optional key to track specific loading operations
     */
    show(key: string = 'global'): void {
        this.loadingMap.set(key, true);
        this.updateLoadingState();
    }

    /**
     * Hide loading indicator
     * @param key Optional key to track specific loading operations
     */
    hide(key: string = 'global'): void {
        this.loadingMap.delete(key);
        this.updateLoadingState();
    }

    /**
     * Check if loading is active
     */
    isLoading(): boolean {
        return this.loadingSubject.value;
    }

    /**
     * Check if a specific key is loading
     */
    isLoadingKey(key: string): boolean {
        return this.loadingMap.has(key);
    }

    /**
     * Clear all loading states
     */
    clearAll(): void {
        this.loadingMap.clear();
        this.updateLoadingState();
    }

    /**
     * Update the loading state based on the map
     */
    private updateLoadingState(): void {
        const isLoading = this.loadingMap.size > 0;
        this.loadingSubject.next(isLoading);
    }

    /**
     * Execute a function with loading state
     * @param fn Function to execute
     * @param key Optional key to track this operation
     */
    async withLoading<T>(fn: () => Promise<T>, key: string = 'global'): Promise<T> {
        try {
            this.show(key);
            return await fn();
        } finally {
            this.hide(key);
        }
    }

    /**
     * Wrap an Observable with loading state
     * @param key Key to track this operation
     */
    wrapObservable<T>(key: string = 'global') {
        return (source: Observable<T>) => {
            return new Observable<T>(observer => {
                this.show(key);
                const subscription = source.subscribe({
                    next: (value) => observer.next(value),
                    error: (error) => {
                        this.hide(key);
                        observer.error(error);
                    },
                    complete: () => {
                        this.hide(key);
                        observer.complete();
                    }
                });
                return () => subscription.unsubscribe();
            });
        };
    }
}
